

import React, { useState, useEffect, useContext, useCallback } from "react";
import toast from "react-hot-toast";

import Pagination, { usePagination } from "../../../Components/CommonComponent/Pgination/pagination";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaEdit } from "react-icons/fa";

import AddButton from "../../../Components/CommonComponent/Button/AddButton";
import CreateGroup from "./CreateGroup";
import DeleteDataModal from "../../../Components/CommonComponent/Modals/DeleteDataModal";
import { CommonContext } from "../../../App";
import { useTranslation } from "react-i18next";
import api from "../../../services/api";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";

export default function AddGroup() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState({ allData: [], pagination: { totalRecords: 0 } });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupNameInHindi, setGroupNameInHindi] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchDataLoading, setfetechDataLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkSearchTerm, setCheckSearchTerm] = useState(false);

  const types = [
    { name: "Assets", nameInHindi: "सम्पत्ति, लेनदारी" },
    { name: "Liability", nameInHindi: "दायित्व, देनदारी" },
    { name: "Income", nameInHindi: "कमाई, बिक्री" },
    { name: "Expense", nameInHindi: "खर्च, खरीद" },
  ];

  const [groupValidation, setGroupValidation] = useState(false);
  const [groupType, setGroupType] = useState(types[0]);
  const [groupTypeValidation, setGroupTypeValidation] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const { branchId } = useContext(CommonContext);
  const [language, setLanguage] = useState("en");

  // ✅ Batch state — lifted here so AddGroup controls the full batch
  const [groupBatch, setGroupBatch] = useState([]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("second_language"));
    if (stored?.language) setLanguage(stored.language);
  }, []);

  const {
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    getPaginatedData,
  } = usePagination(10);

  const paginationData = getPaginatedData(groups);

  useEffect(() => {
    if (branchId) fetchGroups();
  }, [currentPage, itemsPerPage, branchId]);

  const fetchGroups = async (search = "") => {
    setfetechDataLoading(true);
    try {
      const res = await api.get(
        `groups-types/get-all-group?page=${currentPage}&limit=${itemsPerPage}&branchId=${branchId}&type=Assets,Income,Expense,Liability&search=${search}`
      );
      if (res.data.success) {
        const payload = res.data;
        const allData = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.allData)
          ? payload.allData
          : [];
        const totalRecords =
          payload?.totalRecords ?? payload?.pagination?.totalRecords ?? allData.length;
        setGroups({ allData, pagination: { totalRecords } });
      }
    } catch (err) {
      console.error("Error fetching groups", err);
    } finally {
      setfetechDataLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (searchTerm.length === 0) setCheckSearchTerm(false);
  };

  useEffect(() => {
    if (checkSearchTerm && searchTerm.trim() === "") {
      handlePageChange(1);
      fetchGroups("");
    }
  }, [searchTerm]);

  const getSerialNumber = useCallback(
    (index) => (currentPage - 1) * itemsPerPage + index + 1,
    [currentPage, itemsPerPage]
  );

  // ✅ handleSave — handles both single save (edit) and batch save (create)
  const handleSave = async () => {
    if (loading) return;

    // ─── EDIT MODE: single update ─────────────────────────────────────────
    if (editMode && selectedLedger?.id) {
      setLoading(true);
      try {
        const response = await api.post(
          `groups-types/update-group?id=${selectedLedger.id}`,
          {
            groupName,
            groupNameInHindi,
            type: groupType.name,
            typeInHindi: groupType.nameInHindi,
            branchId,
          }
        );
        if (response.data?.success) {
          toast.success(response.data?.message || "Group updated");
          fetchGroups();
          handleCancel();
        } else {
          toast.error(response.data?.message || "Update failed");
        }
      } catch (err) {
        console.error("Error updating group", err);
        toast.error(err?.response?.data?.message || "error updating group");
      } finally {
        setLoading(false);
      }
      return;
    }

    // ─── CREATE MODE: batch or single ────────────────────────────────────
    // Build the final list to send
    let finalBatch = [...groupBatch];

    // If current form has data and it's not already represented as an editing item,
    // add it to the batch
    if (groupName.trim() && groupType) {
      const currentItem = {
        groupName,
        groupNameInHindi,
        type: groupType.name,
        typeInHindi: groupType.nameInHindi,
      };
      finalBatch.push(currentItem);
    }

    if (finalBatch.length === 0) {
      toast.error("Please add at least one group");
      return;
    }

    // Strip internal _groupType key before sending
    const cleanBatch = finalBatch.map(({ _groupType, ...rest }) => rest);

    setLoading(true);
    try {
      const response = await api.post(`groups-types/new-group`, {
        branchId,
        groups: cleanBatch,
      });

      if (response.data?.success) {
        toast.success(
          response.data?.message ||
            `${cleanBatch.length} group${cleanBatch.length > 1 ? "s" : ""} added successfully`
        );
        setGroupBatch([]);
        fetchGroups();
        handleCancel();
      } else {
        toast.error(response.data?.message || "Operation failed");
      }
    } catch (err) {
      console.error("Error saving groups", err);
      toast.error(err?.response?.data?.message || "error saving groups");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setGroupTypeValidation(false);
    setGroupValidation(false);
    setIsModalOpen(false);
    setGroupName("");
    setGroupNameInHindi("");
    setGroupType(types[0]);
    setSelectedLedger(null);
    setEditMode(false);
    setGroupBatch([]);
  };

  const handleAddGroup = () => {
    const trimmedValue = searchTerm.trim();
    handlePageChange(1);
    if (trimmedValue.length > 0) {
      fetchGroups(trimmedValue);
      setCheckSearchTerm(true);
    } else {
      setIsModalOpen(true);
      setCheckSearchTerm(false);
    }
  };

  const handleEditLedger = useCallback(
    (group) => {
      setSelectedLedger(group);
      setGroupName(group.groupName || group.category || "");
      setGroupNameInHindi(group.groupNameInHindi || "");
      const typeObj = types.find((t) => t.name === group.type) || types[0];
      setGroupType(typeObj);
      setIsModalOpen(true);
      setEditMode(true);
    },
    [types]
  );

  const handleDeleteLedger = useCallback((ledger) => {
    setSelectedLedger(ledger);
    setModalDelete(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setModalDelete(false);
    setSelectedLedger(null);
  }, []);

  return (
    <div className="py-2 flex justify-center">
      <div className="flex flex-col justify-center w-full">
        <div className="flex justify-between py-[2px] items-center bg-theme-headerBg px-2 text-theme-color">
          <div className="font-medium">{t("group")}</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search group..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="px-2 py-[2px] text-sm rounded-1 border border-gray-300 focus:outline-none text-black outline-none"
            />
            <AddButton
              onClick={handleAddGroup}
              text={searchTerm.trim() ? "Search" : t("add")}
            />
          </div>
        </div>

        {fetchDataLoading ? (
          <div className="flex justify-center my-4">
            <LoadingSpinner size={70} color="#be8024" />
          </div>
        ) : (groups?.pagination?.totalRecords ?? 0) === 0 ? (
          <p className="text-gray-500 italic text-center mt-5 pt-3">{t("noDataAvailable")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapserounded-lg border-separate border-spacing-0 rounded-b text-sm mb-20">
              <thead>
                <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                  <th className="border-b font-medium px-1 text-center">{t("srNumber")}</th>
                  <th className="border-b py-[2px] font-medium px-1 text-left">{t("groupName")}</th>
                  <th className="border-b font-medium px-1 text-center">{t("type")}</th>
                  <th className="border-b font-medium px-1 text-center">{t("action")}</th>
                </tr>
              </thead>
              <tbody>
                {paginationData.currentItems?.map((group, index) => (
                  <tr
                    key={group.id || group.groupId}
                    className="bg-theme-tableBg hover:bg-theme-tableHoverColor"
                  >
                    <td className="border-b px-1 text-center">{getSerialNumber(index)}</td>
                    <td className="border-b py-[2px] px-1 capitalize">
                      {group.groupName || group.category || "-"}{" "}
                      {language !== "en" && group.groupNameInHindi && `(${group.groupNameInHindi})`}
                    </td>
                    <td className="border-b px-1 text-center">
                      {group.type || ""}{" "}
                      {language !== "en" &&
                        (group.type_in_hindi || group.typeInHindi) &&
                        `(${group.type_in_hindi || group.typeInHindi})`}
                    </td>
                    <td className="border-b px-1">
                      <div className="flex justify-center gap-2">
                        <FaEdit
                          title="Edit group"
                          onClick={group.branch_id !== 0 ? () => handleEditLedger(group) : undefined}
                          className={
                            group.branch_id !== 0
                              ? "text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                              : "text-gray-400 mt-1 opacity-50 pointer-events-none"
                          }
                        />
                        <RiDeleteBin6Fill
                          title="Delete group"
                          onClick={group.branch_id !== 0 ? () => handleDeleteLedger(group) : undefined}
                          className={
                            group.branch_id !== 0
                              ? "text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                              : "text-red-400 mt-1 opacity-50 pointer-events-none"
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              data={groups}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 50, 100]}
              showItemsPerPageSelector={true}
              showPageInfo={true}
              position="bottom-center"
            />
          </div>
        )}
      </div>

      {/* ✅ Create / Edit Group Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/20 bg-opacity-50 z-[1000]">
          <CreateGroup
            groupName={groupName}
            setGroupName={setGroupName}
            groupNameInHindi={groupNameInHindi}
            setGroupNameInHindi={setGroupNameInHindi}
            groupValidation={groupValidation}
            groupType={groupType}
            setGroupType={setGroupType}
            groupTypeValidation={groupTypeValidation}
            setGroupTypeValidation={setGroupTypeValidation}
            types={types}
            handleSave={handleSave}
            handleCancel={handleCancel}
            setGroupValidation={setGroupValidation}
            selectedLedger={selectedLedger}
            addMoreButton={true}   // show Add More button
            loading={loading}
            setLoading={setLoading}
            editMode={editMode}
            // ✅ pass batch state
            groupBatch={groupBatch}
            setGroupBatch={setGroupBatch}
          />
        </div>
      )}

      {modalDelete && (
        <DeleteDataModal
          selectedLedger={selectedLedger}
          handleDeleteModalClose={handleDeleteModalClose}
          handleDeleteLedgerApi={async (ledger) => {
            try {
              const res = await api.post(`groups-types/delete-group?id=${ledger.id}`, {
                isDelete: 1,
                branchId,
              });
              if (res.data.success) {
                toast.success(res.data.message || "Group deleted successfully");
                fetchGroups();
              } else {
                toast.error(res.data.message || "Failed to delete group");
              }
            } catch (err) {
              console.error("Error deleting Group", err);
              toast.error(err?.response?.data?.message || "Failed to delete group");
            } finally {
              handleDeleteModalClose();
            }
          }}
        />
      )}
    </div>
  );
}